import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { api } from '../api/client';

const BetaContext = createContext(null);
export const useAdrBeta = () => useContext(BetaContext);

function SessionBetaProvider({ children, active }) {
  const [state, setState] = useState({ loading: active, saving: false, error: '', adrBetaOptIn: false, adrBetaEnabled: false });
  const generation = useRef(0);
  const busy = useRef(false);
  const refresh = useCallback(async () => {
    if (!active || busy.current) return;
    const request = ++generation.current;
    setState(s => ({ ...s, loading: true, error: '' }));
    try {
      const result = await api.features();
      if (request === generation.current) setState({ ...result, loading: false, saving: false, error: '' });
    } catch (error) {
      if (request === generation.current) setState(s => ({ ...s, adrBetaEnabled: false, loading: false, error: error.message }));
    }
  }, [active]);
  const cancelPending = useCallback(() => { ++generation.current; }, []);
  useEffect(() => {
    refresh();
    return cancelPending;
  }, [refresh, cancelPending]);
  const update = async (enabled) => {
    if (!active || busy.current) return;
    busy.current = true;
    const request = ++generation.current;
    setState(s => ({ ...s, saving: true, error: '' }));
    try {
      const result = await api.updateFeatures(enabled);
      if (request === generation.current) setState({ ...result, loading: false, saving: false, error: '' });
    } catch (error) {
      if (request === generation.current) setState(s => ({ ...s, saving: false, error: error.message }));
    } finally { busy.current = false; }
  };
  return <BetaContext.Provider value={{ ...state, refresh, update }}>{children}</BetaContext.Provider>;
}

export default function BetaProvider({ children }) {
  const session = useSelector(s => s.auth.sessionID);
  const authenticated = useSelector(s => s.auth.isAuthenticated);
  const active = Boolean(authenticated && session);
  // Remount on identity/session changes so a previous user's preference cannot leak.
  return <SessionBetaProvider key={active ? session : 'signed-out'} active={active}>{children}</SessionBetaProvider>;
}
